import { useSize, useUpdateEffect } from 'ahooks'
import { number } from 'echarts/core'
import React, { useEffect, useRef } from 'react'
import st from './index.scss'

/**
 * 水印组件（因为水印和视频关联性比较强，所以写在了这里）
 * @param {*} color 水印颜色
 * @param {*} fontSize 水印字体大小
 * @param {*} value 水印名称
 * @param {*} tcPlayer 播放器
 * @param {*} initWatermark 重新渲染方法
 * @returns
 */
export default function Watermark({
    color,
    fontSize,
    value,
    tcPlayer,
    initWatermark
}) {
    const canvasRef = useRef()
    const size = useSize(canvasRef)
    const playerSize = useSize(document.getElementById('player-container-id'))
    // 画布
    let canvasContext = useRef('')
    // 是否已经渲染水印了
    const watermarkShow = useRef(false)
    // 速度
    const speed = [0.6, 0.8, 1, 1.2, 1.4, 1.5, 1.7]
    // 随机方向x
    const randomX = [0.1, 0.2, 0.3, -0.1, -0.2, -0.3]
    // 随机方向y
    const randomY = [0.1, 0.2, 0.3, -0.1, -0.2, -0.3]
    // 水印移动方向（只有x 和 y轴）
    const direction = {
        x: () => randomX[Math.floor(Math.random() * randomX.length)], // 随机区间
        y: () => randomY[Math.floor(Math.random() * randomY.length)] // 随机区间
    }
    // 当前速度
    const currentSpeed = useRef('')
    // 当前移动方向
    const currentDirection = useRef('')
    // 当前位置
    const currentPosition = useRef('')
    // 水印文字宽度、高度
    const watermarkInfo = useRef('')
    // 随机水印的时长
    const randomTime = useRef('')
    // 根据屏幕刷新执行
    const requestAnimationFrameRef = useRef()
    // 视频宽高比例
    const videoBili = useRef()

    // 因为修改canvas 的宽高，会导致绘制的内容清除，所以在修改canvas宽高的时候重新绘制
    useEffect(() => {
        canvasRef.current.width = size?.width
        canvasRef.current.height = size?.height
        // 视频大小改变，重新计算水印画布大小比例
        getVideoBili()
    }, [size, playerSize?.width, playerSize?.height])

    // 获取canvas
    useEffect(() => {
        canvasContext.current = canvasRef?.current?.getContext('2d')

        // 随机速度
        currentSpeed.current = speed[Math.floor(Math.random() * speed.length)]

        // 随机方向
        currentDirection.current = {
            x: direction.x(),
            y: direction.y()
        }

        watermarkInfo.current = getWatermarkInfo()

        // 渲染水印
        initCanvas()
    }, [])

    // 初始化画布
    function initCanvas() {
        requestAnimationFrameRef.current = requestAnimationFrame(initCanvas)

        // 检测水印是否存在，更新水印样式
        detectionWatermark(tcPlayer)

        // 已经计算过视频比例，更新过画布大小
        if (videoBili.current) {
            // 水印已经渲染
            if (watermarkShow.current) {
                // 4分钟变更一下初始位置
                if ((Math.floor(tcPlayer.currentTime()) / 60) % 4 == 0) {
                    if (
                        randomTime.current !=
                        Math.floor(tcPlayer.currentTime() / 60)
                    ) {
                        randomTime.current =
                            Math.floor(tcPlayer.currentTime()) / 60
                        // 清空画布
                        clear()
                        // 随机速度
                        currentSpeed.current =
                            speed[Math.floor(Math.random() * speed.length)]
                        // 随机方向
                        currentDirection.current = {
                            x: direction.x(),
                            y: direction.y()
                        }
                        // 随机位置
                        renderWatermark(randomPosition().x, randomPosition().y)
                    } else {
                        // 碰撞检测、移动
                        collisionDetection(
                            currentPosition.current.x +
                            currentDirection.current.x *
                            currentSpeed.current,
                            currentPosition.current.y +
                            currentDirection.current.y *
                            currentSpeed.current
                        )
                    }
                } else {
                    randomTime.current = ''
                    // 碰撞检测、移动
                    collisionDetection(
                        currentPosition.current.x +
                        currentDirection.current.x * currentSpeed.current,
                        currentPosition.current.y +
                        currentDirection.current.y * currentSpeed.current
                    )
                }
            } else {
                // 随机位置
                renderWatermark(randomPosition().x, randomPosition().y)
                watermarkShow.current = true
            }
        }
    }

    // 碰撞检测 x、y 下次要移动的位置
    function collisionDetection(x, y) {
        const canvasInfo = getCanvasInfo()
        // console.log('速率:' + currentSpeed.current + '随机方向:x' + currentDirection.current.x + ',y:' + currentDirection.current.y)

        // 超出屏幕的最大值x
        let maxX =
            x <= 0
                ? 0
                : x >= canvasInfo.width - watermarkInfo.current.width
                    ? canvasInfo.width - watermarkInfo.current.width
                    : x
        // 超出屏幕的最大值y
        let maxY = y <= 0 ? 0 : y >= canvasInfo.height ? canvasInfo.height : y

        // console.log(maxX, maxY)

        renderWatermark(maxX, maxY)

        // 碰撞检测
        if (
            x <= 0 ||
            y <= watermarkInfo.current.height ||
            x >= canvasInfo.width - watermarkInfo.current.width ||
            y >= canvasInfo.height
        ) {
            // 碰撞折射
            currentDirection.current = {
                x:
                    x >= canvasInfo.width - watermarkInfo.current.width ||
                        x <= 0
                        ? -currentDirection.current.x
                        : currentDirection.current.x,
                y:
                    y >= canvasInfo.height - watermarkInfo.current.height ||
                        y <= watermarkInfo.current.height
                        ? -currentDirection.current.y
                        : currentDirection.current.y
            }

            // console.log('超出屏幕')
        }
    }

    // 获取canvas 宽高
    function getCanvasInfo() {
        return {
            width: canvasRef?.current?.clientWidth,
            height: canvasRef?.current?.clientHeight
        }
    }

    // 随机位置 水印展示位置规则（视频 左边-100，右边-100，上边-100，下边-100区域进行随机）
    function randomPosition() {
        // 水印内边距 （-110 是为了让水印初始化的时候不要在黑色区域，tcPlayer 有个 videoWidth 和 videoHeight 方法可以获取到视频可视区域的宽度高度，有时间可以优化一下）
        const watermarkPadding = 110
        const canvasInfo = getCanvasInfo()

        // 随机x、y轴
        let x = Math.floor(
            Math.random() *
            (canvasInfo.width -
                watermarkInfo.current.width -
                watermarkPadding * 2) +
            watermarkPadding
        )
        let y = Math.floor(
            Math.random() *
            (canvasInfo.height -
                watermarkInfo.current.height -
                watermarkPadding * 2) +
            watermarkPadding
        )

        return {
            x,
            y
        }
    }

    // 根据视频分辨率的宽度、高度渲染画布的大小（这块只能获得视频分辨率的比例，需要自己计算一下）
    function getVideoBili() {
        // 画布父盒子dom
        let player_container = document.getElementById('player-container-id')
        if (!player_container) return

        let widthBili = player_container.offsetWidth / tcPlayer.videoWidth()
        let heightBili = player_container.offsetHeight / tcPlayer.videoHeight()

        // 获取计算比例
        if (
            widthBili * tcPlayer?.videoWidth() <=
            player_container.offsetWidth &&
            tcPlayer?.videoHeight() * widthBili <= player_container.offsetHeight
        ) {
            videoBili.current = widthBili
        } else if (
            heightBili * tcPlayer?.videoWidth() <=
            player_container.offsetWidth &&
            tcPlayer?.videoHeight() * heightBili <=
            player_container.offsetHeight
        ) {
            videoBili.current = heightBili
        }
    }

    // 设置样式防止有人修改水印，导致水印不展示
    function setCanvasWidthHeight() {
        // 如果已经计算过比例不在计算了，耗费性能
        if (!videoBili.current) {
            getVideoBili()
        }

        // 切换视频的时候videoWidth、videoHeight会为0，所以判断一下
        if (tcPlayer.videoWidth() > 0 && tcPlayer.videoHeight() > 0) {
            // 根据视频分辨率 设置画布父级宽高
            document.getElementById('watermark_canvas').style = ''
            document.getElementById('watermark_canvas').style.display = 'block'
            document.getElementById('watermark_canvas').style.width =
                tcPlayer.videoWidth() * videoBili.current + 'px'
            document.getElementById('watermark_canvas').style.height =
                tcPlayer.videoHeight() * videoBili.current + 'px'
            document.getElementById('watermark_canvas').style.position =
                'absolute'
            document.getElementById('watermark_canvas').style.left = '0'
            document.getElementById('watermark_canvas').style.right = '0'
            document.getElementById('watermark_canvas').style.top = '0'
            document.getElementById('watermark_canvas').style.bottom = '0'
            document.getElementById('watermark_canvas').style.margin = 'auto'
            document.getElementById('watermark_canvas').style.pointerEvents =
                'none'
            document.getElementById('watermark_canvas').style.opacity = '1'
        }

        // 设置画布样式防止别人修改水印隐藏
        document.getElementById('watermark_canvas_dom').style = ''
        document.getElementById('watermark_canvas_dom').style.width = '100%'
        document.getElementById('watermark_canvas_dom').style.height = '100%'
        document.getElementById('watermark_canvas_dom').style.position =
            'absolute'
        document.getElementById('watermark_canvas_dom').style.display = 'block'
        document.getElementById('watermark_canvas_dom').style.opacity = '1'
        document.getElementById('watermark_canvas_dom').style.top = '0'
        document.getElementById('watermark_canvas_dom').style.left = '0'
    }

    // 检测水印是否存在
    function detectionWatermark(tcPlayer) {
        // 在需要渲染水印的时候没有展示水印
        if (
            !document.getElementById('watermark_canvas') ||
            !document.getElementById('watermark_canvas_dom')
        ) {
            console.warn('警告：不要删除水印')
            cancelAnimationFrame(requestAnimationFrameRef.current)
            document.getElementById('watermark_canvas') &&
                document.getElementById('watermark_canvas').remove()
            initWatermark(tcPlayer)
        } else {
            setCanvasWidthHeight()
        }
    }

    // 获取水印宽度
    function getWatermarkInfo() {
        let watermarkWidth, watermarkHeight
        // 求自己的宽度，目的是用来校验当前是否还需要绘制
        let span = document.createElement('span')
        span.innerHTML = value
        span.style.fontSize = fontSize + 'px'
        span.style.position = 'absolute'
        span.style.height = fontSize + 'px'
        span.style.lineHeight = fontSize + 'px'
        document.body.appendChild(span)
        // 记录弹幕宽度
        watermarkWidth = span.clientWidth
        watermarkHeight = span.clientHeight
        document.body.removeChild(span)

        return {
            width: watermarkWidth,
            height: watermarkHeight
        }
    }

    // 绘制画布
    function renderWatermark(x, y, updateOpsition = true) {
        clear()

        // 是否更新画布大小记录、水印位置
        if (updateOpsition) {
            currentPosition.current = {
                x,
                y
            }
        }

        // 求自己的宽度，目的是用来校验当前是否还需要绘制
        canvasContext.current.font = fontSize + 'px "Microsoft YaHei"'
        canvasContext.current.fillStyle = color
        canvasContext.current.fillText(value, x, y)
    }

    // 清空画布
    function clear() {
        // 清空画布
        canvasContext.current.clearRect(
            0,
            0,
            canvasRef.current.offsetWidth,
            canvasRef.current.offsetHeight
        )
    }

    return (
        <canvas
            ref={canvasRef}
            id="watermark_canvas_dom"
            className={st['canvas-box']}
        ></canvas>
    )
}
